import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { formatCurrency, formatDate } from '../lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, 
  MoreVertical, 
  Pencil, 
  Trash2, 
  Package,
  Filter,
  Sofa,
  Tv,
  Wrench,
  Box,
  AlertTriangle,
  CheckCircle,
  Upload,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryConfig = {
  furniture: { label: 'Furniture', icon: Sofa },
  elektronik: { label: 'Elektronik', icon: Tv },
  perlengkapan: { label: 'Perlengkapan', icon: Box },
  lainnya: { label: 'Lainnya', icon: Package },
};

const conditionConfig = {
  baik: { label: 'Baik', color: 'bg-emerald-100 text-emerald-700' },
  rusak_ringan: { label: 'Rusak Ringan', color: 'bg-amber-100 text-amber-700' },
  rusak_berat: { label: 'Rusak Berat', color: 'bg-red-100 text-red-700' },
};

const FacilityForm = ({ facility, rooms, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: facility?.name || '',
    category: facility?.category || 'furniture',
    quantity: facility?.quantity || 1,
    condition: facility?.condition || 'baik',
    room_id: facility?.room_id || '',
    purchase_date: facility?.purchase_date || '',
    purchase_price: facility?.purchase_price || '',
    notes: facility?.notes || '',
    image_url: facility?.image_url || ''
  });
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipe file tidak didukung. Gunakan JPG, PNG, atau WebP');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload/room-image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const imageUrl = response.data.url.startsWith('http') 
          ? response.data.url 
          : `${API_URL}${response.data.url}`;
        setFormData(prev => ({ ...prev, image_url: imageUrl }));
        toast.success('Gambar berhasil diupload');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(error.response?.data?.detail || 'Gagal mengupload gambar');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image_url: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Mohon isi nama fasilitas');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        quantity: parseInt(formData.quantity),
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
        room_id: formData.room_id || null
      };

      if (facility?.id) {
        await axios.put(`${API_URL}/api/facilities/${facility.id}`, payload);
        toast.success('Fasilitas berhasil diperbarui');
      } else {
        await axios.post(`${API_URL}/api/facilities`, payload);
        toast.success('Fasilitas berhasil ditambahkan');
      }
      onSave();
    } catch (error) {
      console.error('Error saving facility:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan fasilitas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Nama Fasilitas *</Label>
          <Input
            id="name"
            name="name"
            placeholder="Contoh: Kasur Single"
            value={formData.name}
            onChange={handleChange}
            className="h-12 bg-muted border-0 rounded-xl"
            data-testid="facility-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="category">Kategori</Label>
          <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
            <SelectTrigger className="h-12 bg-muted border-0 rounded-xl" data-testid="facility-category-select">
              <SelectValue placeholder="Pilih kategori" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(categoryConfig).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="quantity">Jumlah</Label>
          <Input
            id="quantity"
            name="quantity"
            type="number"
            min="1"
            value={formData.quantity}
            onChange={handleChange}
            className="h-12 bg-muted border-0 rounded-xl"
            data-testid="facility-quantity-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="condition">Kondisi</Label>
          <Select value={formData.condition} onValueChange={(value) => setFormData(prev => ({ ...prev, condition: value }))}>
            <SelectTrigger className="h-12 bg-muted border-0 rounded-xl" data-testid="facility-condition-select">
              <SelectValue placeholder="Pilih kondisi" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(conditionConfig).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="room_id">Lokasi (Kamar)</Label>
        <Select value={formData.room_id || "umum"} onValueChange={(value) => setFormData(prev => ({ ...prev, room_id: value === "umum" ? "" : value }))}>
          <SelectTrigger className="h-12 bg-muted border-0 rounded-xl" data-testid="facility-room-select">
            <SelectValue placeholder="Pilih lokasi" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="umum">Fasilitas Umum / Gedung</SelectItem>
            {rooms.map((room) => (
              <SelectItem key={room.id} value={room.id}>{room.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="purchase_date">Tanggal Beli</Label>
          <Input
            id="purchase_date"
            name="purchase_date"
            type="date"
            value={formData.purchase_date}
            onChange={handleChange}
            className="h-12 bg-muted border-0 rounded-xl"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="purchase_price">Harga Beli (Rp)</Label>
          <Input
            id="purchase_price"
            name="purchase_price"
            type="number"
            placeholder="0"
            value={formData.purchase_price}
            onChange={handleChange}
            className="h-12 bg-muted border-0 rounded-xl"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Catatan</Label>
        <Textarea
          id="notes"
          name="notes"
          placeholder="Catatan tambahan..."
          value={formData.notes}
          onChange={handleChange}
          className="bg-muted border-0 rounded-xl resize-none"
          rows={2}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-sm">Gambar (opsional)</Label>
        {formData.image_url ? (
          <div className="relative">
            <img
              src={formData.image_url}
              alt="Preview"
              className="w-full h-28 object-cover rounded-xl"
            />
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2 rounded-full w-7 h-7"
              onClick={removeImage}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-muted-foreground/25 rounded-xl cursor-pointer hover:border-primary/50 transition-colors bg-muted/50">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleImageUpload}
              className="hidden"
              disabled={uploadingImage}
            />
            {uploadingImage ? (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 spinner" />
                <span className="text-xs text-muted-foreground">Mengupload...</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Upload gambar</span>
              </div>
            )}
          </label>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
          Batal
        </Button>
        <Button type="submit" disabled={loading} className="rounded-xl" data-testid="facility-submit-btn">
          {loading ? <div className="w-5 h-5 spinner" /> : facility?.id ? 'Simpan' : 'Tambah'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const Fasilitas = () => {
  const [facilities, setFacilities] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterCondition, setFilterCondition] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(null);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (filterCategory) params.append('category', filterCategory);
      if (filterCondition) params.append('condition', filterCondition);
      
      const [facilitiesRes, roomsRes] = await Promise.all([
        axios.get(`${API_URL}/api/facilities?${params.toString()}`),
        axios.get(`${API_URL}/api/rooms`)
      ]);
      setFacilities(facilitiesRes.data);
      setRooms(roomsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCategory, filterCondition]);

  const handleEdit = (facility) => {
    setSelectedFacility(facility);
    setDialogOpen(true);
  };

  const handleDelete = (facility) => {
    setSelectedFacility(facility);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/facilities/${selectedFacility.id}`);
      toast.success('Fasilitas berhasil dihapus');
      fetchData();
    } catch (error) {
      toast.error('Gagal menghapus fasilitas');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedFacility(null);
    }
  };

  const handleSave = () => {
    setDialogOpen(false);
    setSelectedFacility(null);
    fetchData();
  };

  const totalFacilities = facilities.reduce((sum, f) => sum + f.quantity, 0);
  const damagedCount = facilities.filter(f => f.condition !== 'baik').reduce((sum, f) => sum + f.quantity, 0);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="fasilitas-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Inventaris Fasilitas</h1>
          <p className="text-muted-foreground mt-1">Kelola fasilitas dan inventaris kost</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="rounded-xl" data-testid="add-facility-btn">
          <Plus className="w-5 h-5 mr-2" />
          Tambah Fasilitas
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <Package className="w-4 h-4 text-primary" />
            <span className="text-xs text-muted-foreground">Total Item</span>
          </div>
          <p className="text-2xl font-bold">{totalFacilities}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-emerald-600" />
            <span className="text-xs text-muted-foreground">Kondisi Baik</span>
          </div>
          <p className="text-2xl font-bold text-emerald-600">{totalFacilities - damagedCount}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-xs text-muted-foreground">Perlu Perbaikan</span>
          </div>
          <p className="text-2xl font-bold text-amber-600">{damagedCount}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <Box className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Jenis Item</span>
          </div>
          <p className="text-2xl font-bold">{facilities.length}</p>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Filter:</span>
        </div>
        <Select value={filterCategory || "all"} onValueChange={(v) => setFilterCategory(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[150px] h-10 bg-muted border-0 rounded-xl">
            <SelectValue placeholder="Kategori" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kategori</SelectItem>
            {Object.entries(categoryConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterCondition || "all"} onValueChange={(v) => setFilterCondition(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[150px] h-10 bg-muted border-0 rounded-xl">
            <SelectValue placeholder="Kondisi" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kondisi</SelectItem>
            {Object.entries(conditionConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {loading ? (
        <Card className="p-6">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        </Card>
      ) : facilities.length === 0 ? (
        <Card className="p-8">
          <div className="empty-state">
            <Package className="empty-state-icon" />
            <p className="empty-state-title">Belum ada fasilitas</p>
            <p className="empty-state-description">Tambahkan inventaris fasilitas kost</p>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Nama</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead>Lokasi</TableHead>
                <TableHead>Jumlah</TableHead>
                <TableHead>Kondisi</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {facilities.map((facility) => {
                const catConfig = categoryConfig[facility.category] || categoryConfig.lainnya;
                const condConfig = conditionConfig[facility.condition] || conditionConfig.baik;
                const Icon = catConfig.icon;
                return (
                  <TableRow key={facility.id} data-testid={`facility-row-${facility.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                          <Icon className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="font-medium">{facility.name}</p>
                          {facility.notes && <p className="text-xs text-muted-foreground">{facility.notes}</p>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{catConfig.label}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{facility.room_name || 'Umum'}</Badge>
                    </TableCell>
                    <TableCell>{facility.quantity}</TableCell>
                    <TableCell>
                      <Badge className={condConfig.color}>{condConfig.label}</Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(facility)}>
                            <Pencil className="w-4 h-4 mr-2" />Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(facility)} className="text-destructive">
                            <Trash2 className="w-4 h-4 mr-2" />Hapus
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedFacility ? 'Edit Fasilitas' : 'Tambah Fasilitas Baru'}
            </DialogTitle>
            <DialogDescription>
              {selectedFacility ? 'Perbarui data fasilitas' : 'Tambahkan inventaris fasilitas'}
            </DialogDescription>
          </DialogHeader>
          <FacilityForm
            facility={selectedFacility}
            rooms={rooms}
            onSave={handleSave}
            onClose={() => { setDialogOpen(false); setSelectedFacility(null); }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Fasilitas?</AlertDialogTitle>
            <AlertDialogDescription>
              Anda yakin ingin menghapus &quot;{selectedFacility?.name}&quot;?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Fasilitas;
